import { ReplaceOrUpdateArrayRequest } from '../@types';

function updateAt<T>(collection: Array<T>, index: number, handler: (entry: T) => T): Array<T> {
	const target = collection[ index ];

	if (!target) {
		return collection;
	}

	return [ ...collection.slice(0, index), handler(target), ...collection.slice(index + 1) ];
}

export interface RebuildArrayOptions<T> {
	afterReorder?: (entry: T, index: number) => T;
	idSelector?: (entry: T) => number;
}

export function rebuildArray<T>(
	existingArray: Array<T>,
	update?: ReplaceOrUpdateArrayRequest<T>,
	options: RebuildArrayOptions<T> = {}
): Array<T> {
	if (!update) {
		return existingArray;
	}

	if (Array.isArray(update)) {
		return update;
	}

	const { afterReorder, idSelector } = options;
	let updatedArray = existingArray;

	if (update.toAdd && !existingArray.includes(update.toAdd)) {
		updatedArray = [ ...existingArray, update.toAdd ];
	}

	if (update.toRemove) {
		return updatedArray.filter((entry) => entry !== update.toRemove);
	}

	if (update.toRemoveById && idSelector) {
		return updatedArray.filter((entry) => idSelector(entry) !== update.toRemoveById);
	}

	if (update.toUpdateAll) {
		updatedArray = updatedArray.map((entry) => {
			if (typeof update.toUpdateAll === 'function') {
				return {
					...update.toUpdateAll(entry)
				};
			}

			return {
				...entry,
				...update.toUpdateAll
			};
		});
	}

	if (update.toUpdateById && idSelector) {
		const { id, update: idUpdate } = update.toUpdateById;
		const target = updatedArray.findIndex((entry) => idSelector(entry) === id);

		updatedArray = updateAt(updatedArray, target, (entry) => {
			if (typeof idUpdate === 'function') {
				return {
					...idUpdate(entry)
				};
			}

			return {
				...entry,
				...idUpdate
			};
		});
	}

	if (update.toReorderByIndex) {
		const { sourceIndex, targetIndex } = update.toReorderByIndex;

		if (sourceIndex > -1 && targetIndex > -1) {
			const entry = updatedArray[ sourceIndex ];

			if (entry) {
				updatedArray = [ ...updatedArray.slice(0, sourceIndex), ...updatedArray.slice(sourceIndex + 1) ];

				updatedArray.splice(targetIndex, 0, entry);

				if (afterReorder) {
					updatedArray = updatedArray.map(afterReorder);
				}
			}
		}
	}

	return updatedArray;
}
